import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Flame, ArrowRight, ChevronLeft, ChevronRight, MessageCircle, Plus, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { useContent, parseHiddenSet } from "@/hooks/useContent";
import { useEnquiry } from "@/hooks/useEnquiry";
import Seo from "@/components/Seo";

const SHORT_LANDSCAPE = "[@media(orientation:landscape)_and_(max-height:500px)]";
const ENQUIRY_HREF = "/contact?enquiry=1";
const plural = (n) => `${n} flavour${n === 1 ? "" : "s"}`;

function AddToggle({ p, compact = false, testid }) {
  const { has, toggle } = useEnquiry();
  const on = has(p.id);
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); toggle(p); }}
      onKeyDown={(e) => e.stopPropagation()}
      aria-pressed={on}
      aria-label={on ? `Remove ${p.name} from enquiry` : `Add ${p.name} to enquiry`}
      data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 rounded-full border font-bold transition-[background-color,border-color,color,transform] duration-200 active:scale-95 ${
        compact ? "h-9 w-9 shrink-0" : "px-5 py-3.5 text-sm"
      } ${on ? "bg-[#111] border-[#111] text-[#F5B800]" : "bg-white border-black/15 text-[#111] hover:border-black"}`}
    >
      {on ? <Check size={16} strokeWidth={3} /> : <Plus size={16} />}
      {!compact && (on ? "Added to enquiry" : "Add to enquiry")}
    </button>
  );
}

function FlavourStrip({ products, activeId, onPick }) {
  const refs = useRef({});
  const { has } = useEnquiry();
  useEffect(() => {
    refs.current[activeId]?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [activeId]);
  return (
    <div
      className="lg:hidden -mx-4 sm:-mx-6 px-4 sm:px-6 scroll-px-4 sm:scroll-px-6 flex gap-3 overflow-x-auto snap-x snap-mandatory pt-1 pb-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      data-testid="product-strip"
    >
      {products.map((p) => {
        const on = p.id === activeId;
        return (
          <button
            key={p.id}
            ref={(el) => { refs.current[p.id] = el; }}
            onClick={() => onPick(p.id)}
            data-testid={`product-chip-${p.id}`}
            aria-pressed={on}
            className={`relative snap-start shrink-0 flex items-center gap-2 pl-1.5 pr-4 py-1.5 rounded-full border transition-[background-color,border-color,box-shadow] duration-200 ${
              on ? "border-black bg-[#111] text-white shadow-[0_8px_20px_rgba(0,0,0,0.18)]" : "border-black/10 bg-white text-[#111]"
            }`}
          >
            <span className="h-9 w-9 rounded-full overflow-hidden shrink-0" style={{ backgroundColor: p.bg }}>
              <img src={p.image} alt="" className="w-full h-full object-contain p-0.5" />
            </span>
            <span className="font-display font-bold text-sm whitespace-nowrap">{p.name}</span>
            {has(p.id) && (
              <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-[#F5B800] text-[#111] flex items-center justify-center ring-2 ring-[#FDFBF7]" data-testid={`product-chip-added-${p.id}`}>
                <Check size={11} strokeWidth={3} />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function FlavourList({ products, activeId, onPick }) {
  return (
    <div className="hidden lg:block space-y-3" data-testid="product-list">
      {products.map((p) => (
        <div
          key={p.id}
          role="button"
          tabIndex={0}
          onClick={() => onPick(p.id)}
          onKeyDown={(e) => { if (e.key === "Enter") onPick(p.id); }}
          data-testid={`product-tab-${p.id}`}
          className={`group cursor-pointer w-full text-left flex items-center gap-4 p-4 rounded-2xl border transition-[background-color,border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 ${
            activeId === p.id
              ? "border-black bg-white shadow-[0_10px_28px_rgba(0,0,0,0.08)]"
              : "border-black/10 bg-white/60 hover:bg-white"
          }`}
        >
          <div className="h-14 w-14 rounded-xl overflow-hidden shrink-0" style={{ backgroundColor: p.bg }}>
            <img src={p.image} alt={p.name} className="w-full h-full object-contain p-1" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-lg text-[#111]">{p.name}</div>
            <div className="text-xs text-[#6B7280] truncate">{p.tagline}</div>
          </div>
          {p.price && <div className="text-xs font-bold px-2 py-1 rounded-full" style={{ backgroundColor: p.bg, color: p.color }}>
            {p.price}
          </div>}
          <AddToggle p={p} compact testid={`product-list-add-${p.id}`} />
        </div>
      ))}
    </div>
  );
}

function ProductImage({ p, onStep, count }) {
  return (
    <div
      className={`relative min-w-0 overflow-hidden flex items-center justify-center p-6 sm:p-8 aspect-[4/3] md:aspect-auto md:min-h-[420px] lg:min-h-0 lg:aspect-[16/10] ${SHORT_LANDSCAPE}:aspect-auto ${SHORT_LANDSCAPE}:min-h-[260px]`}
      style={{ backgroundColor: p.bg }}
    >
      <img
        src={p.image}
        alt={p.name}
        data-testid="product-detail-image"
        className="max-h-[260px] sm:max-h-[320px] lg:max-h-full max-w-full object-contain drop-shadow-2xl"
      />
      {count > 1 && (
        <div className="lg:hidden absolute inset-x-3 top-1/2 -translate-y-1/2 flex justify-between pointer-events-none">
          {[[-1, ChevronLeft, "prev"], [1, ChevronRight, "next"]].map(([d, Icon, k]) => (
            <button
              key={k}
              onClick={() => onStep(d)}
              aria-label={k === "prev" ? "Previous flavour" : "Next flavour"}
              data-testid={`product-${k}-btn`}
              className="pointer-events-auto h-10 w-10 rounded-full bg-white/85 backdrop-blur border border-black/10 flex items-center justify-center text-[#111] active:scale-95 transition-transform"
            >
              <Icon size={18} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EnquiryActions({ p }) {
  const { items } = useEnquiry();
  return (
    <div className="hidden md:flex flex-wrap items-center gap-3 mt-8">
      <AddToggle p={p} testid="product-add-btn" />
      <Link
        to={items.length ? ENQUIRY_HREF : `/contact?product=${encodeURIComponent(p.name)}`}
        data-testid="product-enquire-btn"
        className="inline-flex items-center justify-center gap-2 rounded-full bg-[#D9381E] hover:bg-[#B02C15] text-white font-bold px-6 py-4 text-sm transition-[background-color,transform] duration-200 active:scale-[0.98]"
      >
        <MessageCircle size={18} /> {items.length ? `Send enquiry (${items.length})` : `Enquire about ${p.name}`} <ArrowRight size={16} />
      </Link>
    </div>
  );
}

function ProductInfo({ p }) {
  return (
    <div className="p-5 sm:p-8 bg-white flex flex-col">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em]" style={{ color: p.color }}>
            Indian Savoury Snack
          </div>
          <h2 className="mt-1 font-display font-black text-3xl sm:text-4xl tracking-tight text-[#111]" data-testid="product-detail-name">
            {p.name}
          </h2>
          <p className="mt-1 text-sm sm:text-base text-[#4B5563]">{p.tagline}</p>
        </div>
        <div className="text-2xl sm:text-3xl font-display font-black shrink-0" style={{ color: p.color }} data-testid="product-detail-price">
          {p.price}
        </div>
      </div>

      {p.ingredients && (
        <div className="mt-5 sm:mt-6">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#111]">Ingredients</div>
          <p className="mt-2 text-sm text-[#4B5563] leading-relaxed">{p.ingredients}</p>
        </div>
      )}

      {p.nutrition && Object.keys(p.nutrition).length > 0 && (
        <div className="mt-5 sm:mt-6">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#111]">Nutritional Info (per 100g)</div>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 md:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="product-nutrition">
            {Object.entries(p.nutrition).map(([k, v]) => (
              <div key={k} className="p-3 rounded-lg bg-[#F3EAD3]">
                <div className="text-[10px] uppercase tracking-widest text-[#6B7280]">{k}</div>
                <div className="font-display font-bold text-base sm:text-lg text-[#111]">{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {p.allergen && (
        <div className="mt-5 sm:mt-6 flex items-center gap-2 text-xs text-[#6B7280]">
          <Flame size={14} /> Allergen: {p.allergen}
        </div>
      )}

      <EnquiryActions p={p} />
    </div>
  );
}

function ProductDetail({ p, onStep, count }) {
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={p.id}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.3 }}
        className={`rounded-3xl overflow-hidden border border-black/10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 ${SHORT_LANDSCAPE}:grid-cols-2`}
        data-testid="product-detail"
      >
        <ProductImage p={p} onStep={onStep} count={count} />
        <ProductInfo p={p} />
      </motion.div>
    </AnimatePresence>
  );
}

function MobileCtaBar({ p }) {
  const { items } = useEnquiry();
  const n = items.length;
  return (
    <div
      className="md:hidden sticky bottom-0 z-30 mt-8 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] bg-[#FDFBF7]/90 backdrop-blur-xl border-t border-black/10"
      data-testid="product-mobile-cta"
    >
      <div className="flex items-center gap-3">
        <div className="min-w-0" data-testid="product-mobile-cta-summary">
          <div className="text-[10px] uppercase tracking-widest text-[#6B7280]">{n ? "Your enquiry" : "Selected"}</div>
          <div className="font-display font-black text-base text-[#111] truncate">{n ? plural(n) : `${p.name}${p.price ? ` · ${p.price}` : ""}`}</div>
        </div>
        <div className="ml-auto shrink-0 flex items-center gap-2">
          <AddToggle p={p} compact testid="product-mobile-add-btn" />
          <Link
            to={n ? ENQUIRY_HREF : `/contact?product=${encodeURIComponent(p.name)}`}
            data-testid="product-mobile-enquire-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#D9381E] text-white font-bold text-sm px-5 py-3.5 active:scale-[0.98] transition-transform"
          >
            {n ? "Send" : "Enquire"} <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}

function EnquiryTray() {
  const { items, remove, clear } = useEnquiry();
  const [footerOn, setFooterOn] = useState(false);
  useEffect(() => {
    const f = document.querySelector("footer");
    if (!f) { return undefined; }
    const io = new IntersectionObserver(([e]) => setFooterOn(e.isIntersecting));
    io.observe(f);
    return () => io.disconnect();
  }, []);
  return (
    <AnimatePresence>
      {items.length > 0 && !footerOn && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          className="hidden xl:block fixed bottom-6 right-6 z-30 w-[340px] rounded-3xl bg-[#111] text-white p-5 shadow-[0_24px_60px_rgba(0,0,0,0.3)]"
          data-testid="enquiry-tray"
        >
          <div className="flex items-center justify-between">
            <div className="font-display font-black text-lg" data-testid="enquiry-tray-count">{plural(items.length)} selected</div>
            <button onClick={clear} data-testid="enquiry-tray-clear" className="text-xs text-white/60 hover:text-white underline-offset-2 hover:underline">Clear</button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2 max-h-28 overflow-y-auto">
            {items.map((i) => (
              <span key={i.id} className="inline-flex items-center gap-1 rounded-full bg-white/10 pl-3 pr-1 py-1 text-xs font-semibold" data-testid={`enquiry-tray-item-${i.id}`}>
                {i.name}
                <button onClick={() => remove(i.id)} aria-label={`Remove ${i.name}`} data-testid={`enquiry-tray-remove-${i.id}`} className="h-5 w-5 rounded-full hover:bg-white/20 flex items-center justify-center">
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
          <Link to={ENQUIRY_HREF} data-testid="enquiry-tray-send" className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D9381E] hover:bg-[#B02C15] text-white font-bold text-sm py-3.5 transition-colors">
            Send enquiry <ArrowRight size={16} />
          </Link>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Products() {
  const [allProducts, setAllProducts] = useState([]);
  const [active, setActive] = useState(null);
  const c = useContent();
  const hidden = parseHiddenSet(c);

  useEffect(() => {
    api.get("/products").then((r) => setAllProducts(r.data)).catch(() => {});
  }, []);

  const products = allProducts.filter((p) => !hidden.has(`product:${p.id}`));
  const selected = products.find((p) => p.id === active) || products[0];
  const step = (d) => {
    const i = products.findIndex((p) => p.id === selected.id);
    setActive(products[(i + d + products.length) % products.length].id);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-0 sm:pt-14 md:py-16 lg:py-20" data-testid="products-page">
      <Seo
        path="/products"
        title="Our Snack Range — 5 Signature Flavours at ₹10 | B-Raw"
        description="Explore B-Raw's hand-crafted snack range from Sirsa — 5 signature flavours at ₹10 a pack, FSSAI certified, small-batch and made with real ingredients."
      />
      <div className="max-w-3xl">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D9381E]">Our Catalog</div>
        <h1 className="mt-2 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#111]">
          Five bold flavours,<br className="hidden sm:block" /> one honest brand.
        </h1>
        <p className="mt-3 sm:mt-4 text-[#4B5563] text-sm sm:text-base md:text-lg leading-relaxed">
          Each B-Raw pouch is small-batch-crafted with real ingredients. Pick a
          flavour to see the full recipe and nutrition, and tap + to add several
          flavours to one enquiry.
        </p>
      </div>

      {selected && (
        <div className="mt-8 md:mt-10 lg:mt-12 grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-6 lg:gap-8">
          <div className="lg:col-span-5 min-w-0">
            <FlavourStrip products={products} activeId={selected.id} onPick={setActive} />
            <FlavourList products={products} activeId={selected.id} onPick={setActive} />
          </div>
          <div className="lg:col-span-7 min-w-0 lg:sticky lg:top-24 self-start">
            <ProductDetail p={selected} onStep={step} count={products.length} />
          </div>
        </div>
      )}
      {selected && <MobileCtaBar p={selected} />}
      <EnquiryTray />
    </div>
  );
}
